import type { FC } from "react";

import { useTranslations } from "@ui/providers/useTranslations";
import dompurify from "isomorphic-dompurify";

import { ResponsiveImage } from "../ResponsiveImage/ResponsiveImage";

import { useCountdown } from "./useCountdown";

import styles from "./Counter.module.scss";

const { sanitize } = dompurify;

type ImageType = {
  src: string;
  alt: string;
  title: string;
};

type BackgroundImageProps = {
  desktopImage: ImageType;
  mediumImage?: ImageType;
  smallImage?: ImageType;
  imageClassName?: string;
};

type CounterProps = {
  date: string;
  renderBackgroundImage?: (props: BackgroundImageProps) => React.ReactNode;
  desktopBackgroundImage?: ImageType;
  tabletBackgroundImage?: ImageType;
  mobileBackgroundImage?: ImageType;
  title?: string;
  description?: string;
};

export const Counter: FC<CounterProps> = (props) => {
  const { __ } = useTranslations();
  const { timeLeft, shouldShowCounter } = useCountdown(props.date);

  return (
    <div className={styles.wrapper}>
      {props?.renderBackgroundImage && props.desktopBackgroundImage?.src ? (
        props.renderBackgroundImage({
          desktopImage: props.desktopBackgroundImage,
          mediumImage: props.tabletBackgroundImage,
          smallImage: props.mobileBackgroundImage,
        })
      ) : (
        <ResponsiveImage
          largeSrc={props.desktopBackgroundImage?.src || ""}
          mediumSrc={props.tabletBackgroundImage?.src}
          smallSrc={props.mobileBackgroundImage?.src}
          className={styles.image}
        />
      )}
      {shouldShowCounter && (
        <div className={styles.counter}>
          <div className={styles.unit}>
            <div className={styles.value}>{timeLeft.days}</div>
            <div className={styles.label}>{__("herobannercountdown.days")}</div>
          </div>
          <div className={styles.unit}>
            <div className={styles.value}>{timeLeft.hours}</div>
            <div className={styles.label}>{__("herobannercountdown.hours")}</div>
          </div>
          <div className={styles.unit}>
            <div className={styles.value}>{timeLeft.minutes}</div>
            <div className={styles.label}>{__("herobannercountdown.minutes")}</div>
          </div>
          <div className={styles.unit}>
            <div className={styles.value}>{timeLeft.seconds}</div>
            <div className={styles.label}>{__("herobannercountdown.seconds")}</div>
          </div>
        </div>
      )}
      <div className={styles.textContainer}>
        {props.title && <div className={styles.title}>{props.title}</div>}
        {props.description && (
          <div
            className={styles.description}
            data-testid="counter-description"
            dangerouslySetInnerHTML={{ __html: sanitize(props.description) }}
          />
        )}
      </div>
    </div>
  );
};
